import { Stepper } from '@mantine/core';
import { useWindowSize } from '../../../../hooks';
import classes from './Stepper.module.css';
import { stepsData } from './stepsData';
import { getGroupAssignmentDate, getMonthSection } from '@web/common';
import type { Bdm2OutcomeResDto } from '@web/common';
import { DateTime } from 'luxon';
import {
  Bdm1Outcome,
  CohortType,
  RetrievalMode,
  StudyGroup,
} from '@prisma/client';
import { MyIcon } from '@web/common-ui';
import { useTranslation } from 'react-i18next';
import { TaskStepperStep } from './TaskStepperStep';
import { StepKey } from './stepTypes';

export type TaskStepperViewProps = {
  currentAction: JSX.Element;
  currentStepKey: StepKey;
  myCohortStartDate: DateTime | null;
  // step 1 states when the study group is announced; null = cohort not known yet
  myCohortOnboardingEndDate?: DateTime | null;
  myCohortType: CohortType;
  studyGroup: StudyGroup | null;
  bdm1SurveyFilledOut: boolean;
  bdm1Outcome: Bdm1Outcome | null;
  bdm1SampleDeadline: DateTime | null;
  // decides the BDM1 outcome paragraph; null = no retrieval row for the kind yet
  retrievalMode: RetrievalMode | null;
  bdm2SurveyDue: boolean;
  bdm2SurveyFilledOut: boolean;
  // the imported BDM2 draw; null while nothing was imported (and for group 2)
  bdm2Outcome?: Bdm2OutcomeResDto | null;
  // pickup deadline of the BDM2 sample, needed by the pickup result copy;
  // null while not loaded
  bdm2SampleDeadline?: DateTime | null;
};

export const TaskStepperView = ({
  currentAction,
  currentStepKey,
  myCohortStartDate,
  myCohortOnboardingEndDate = null,
  myCohortType,
  studyGroup,
  bdm1SurveyFilledOut,
  bdm1Outcome,
  bdm1SampleDeadline,
  retrievalMode,
  bdm2SurveyDue,
  bdm2SurveyFilledOut,
  bdm2Outcome = null,
  bdm2SampleDeadline = null,
}: TaskStepperViewProps) => {
  const isSmallScreen = useWindowSize(900);
  const { t, i18n } = useTranslation('waiting-screen');

  let groupAssignmentDate: string | null = null;
  if (myCohortOnboardingEndDate !== null) {
    const assignment = getGroupAssignmentDate(
      myCohortOnboardingEndDate.toJSDate(),
    );
    groupAssignmentDate = t(
      `Steps.1.AssignmentDate.${getMonthSection(assignment)}`,
      {
        month: assignment
          .setLocale(i18n.language)
          .toLocaleString({ month: 'long' }),
        year: assignment.year,
      },
    );
  }

  const steps =
    myCohortStartDate && myCohortType
      ? stepsData({
          dateTime: myCohortStartDate,
          cohortType: myCohortType,
          groupAssignmentDate,
          studyGroup,
          bdm1SurveyFilledOut,
          bdm1Outcome,
          bdm1SampleDeadline,
          retrievalMode,
          bdm2SurveyDue,
          bdm2SurveyFilledOut,
          bdm2Outcome,
          bdm2SampleDeadline,
          t,
        })
      : [];

  // -1 only when steps is empty or the key is not in this group's array;
  // both fall back to the first step to keep the lookup total.
  const stepIndex = steps.findIndex((step) => step.key === currentStepKey);
  if (stepIndex === -1 && steps.length > 0) {
    // gate drift between the store and stepsData must be visible, not
    // silently render step 0
    console.error(`currentStepKey '${currentStepKey}' not in the step array`);
  }
  const activeStepIndex = stepIndex === -1 ? 0 : stepIndex;

  const activeStep = steps[activeStepIndex];

  return (
    <div>
      {activeStep && (
        <TaskStepperStep
          step={activeStep}
          isSmallScreen={isSmallScreen}
          currentStep={activeStepIndex}
          currentAction={currentAction}
        />
      )}
      <Stepper
        active={activeStepIndex}
        color="#3D7F53"
        orientation="vertical"
        my="20px"
        classNames={classes}
        styles={{
          steps: {
            width: 'auto',
          },
        }}
        completedIcon={<MyIcon name="check" style={{ fill: '#fff' }} />}
      >
        {steps.map((step) => (
          <Stepper.Step
            key={step.key}
            label={step.title}
            description={step.description}
          />
        ))}
      </Stepper>
    </div>
  );
};
